import React, { useEffect, useMemo, useRef, useState } from "react";
import "../style.css";
import { Flex, Text, View, ProgressCircle } from "@adobe/react-spectrum";
import { debounce } from "lodash";
import { formatTimeToMMSS, getInitialsAndColor } from "../../../utils";
import { useSelector } from "react-redux";
import Emitter from "../../../emitter";
import {
  CHAT_ROLES,
  API_CHAT_HISTORY,
  API_DOWNLOAD_ATTACHMENT,
  SHOW_API_STATUS_MESSAGE,
  CHAT_STATES,
} from "../../../constants/constants";
import { ChatMessage, IndividualChat } from "../../../types/chat.type";
import { DownloadIcon } from "../../common/Icons";
import storeRTK from "../../../store/storeRTK";
import { resetUnreadMessagesCount } from "../../../actions/chat";
import { RootState } from "../../../store/storeConfig";
import { chatHistoryAttachment } from "../../../api/session-api";
import { showErrorToast } from "../../../utils/utils";

interface ChatTranscriptProps {
  chatType: "liveChat" | "chatHistory";
  typingStatus: boolean;
  uplodingFiles: any;
  setUploadingFiles?: any;
  activeChatId: string;
}

const ChatTranscript: React.FC<ChatTranscriptProps> = ({
  chatType,
  typingStatus,
  uplodingFiles,
  setUploadingFiles,
  activeChatId,
}) => {
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingAttachments, setDownloadingAttachments] = useState<
    string[]
  >([]);
  const agent = useSelector((state: RootState) => state.agent);
  const chats = useSelector((state: RootState) => state.chat);
  const chatHistory = useSelector((state: RootState) => state.history?.chat);
  const common = useSelector((state: RootState) => state.common);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const isUserAtBottom = useRef(true);

  const chat =
    chatType === "liveChat"
      ? chats.chats.find((ch: IndividualChat) => ch.id === activeChatId)
      : chatHistory.find((ch: IndividualChat) => ch.id === activeChatId);
  const chatRef = useRef(chat);

  const { messages, transcript, customer } = chat || {};

  const { initials, backgroundColor } = useMemo(() => {
    return getInitialsAndColor(customer?.name || "N A") as {
      initials: string;
      backgroundColor: string;
    };
  }, [customer?.name]);

  const callforChatHistory = debounce(() => {
    const currentChat = chatRef.current;
    if (!currentChat?.nextHistoryToken) {
      setLoadingTranscript(false);
    } else {
      setLoadingTranscript(true);
      Emitter.emit(API_CHAT_HISTORY, {
        body: {
          conversationId: currentChat.id,
          nextHistoryToken: currentChat?.nextHistoryToken,
        },
      });
    }
  }, 500);

  const scrollChat = debounce((el: any) => {
    if (el.scrollTop === 0) el.scrollTop = 30;
  }, 1500);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const el = e.target as HTMLElement;

      if (el.scrollTop === 0) {
        callforChatHistory();
        scrollChat(el);
      }

      const threshold = 100;
      const isAtBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      isUserAtBottom.current = isAtBottom;
    };

    const chatElement = document.querySelector(".chat-transcript-section");
    chatElement?.addEventListener("scroll", handleScroll, { passive: true });

    return () => chatElement?.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    chatRef.current = chat;
  }, [chat]);

  useEffect(() => {
    setTimeout(() => {
      if (isUserAtBottom.current && chatEndRef.current) {
        chatEndRef.current.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
      }
      if (chat?.transcript?.length === 0) {
        setLoadingTranscript(false);
      }

      if (chat?.id) {
        storeRTK.dispatch(resetUnreadMessagesCount({ chatID: chat.id }));
      }
      setUploadingFiles?.({});
    }, 100);
  }, [messages?.length, transcript?.length]);

  useEffect(() => {
    const handleDownloadComplete = (data: {
      body: { attachmentId: string };
    }) => {
      const { attachmentId } = data.body;
      setDownloadingAttachments((prev) =>
        prev.filter((id) => id !== attachmentId)
      );
    };

    Emitter.on(SHOW_API_STATUS_MESSAGE, handleDownloadComplete);
    return () => {
      Emitter.off(SHOW_API_STATUS_MESSAGE, handleDownloadComplete);
    };
  }, []);

  const downloadAttachment = async (
    e: React.MouseEvent,
    attachmentId: string,
    contactId: string,
    name: string
  ) => {
    e.preventDefault();
    setIsDownloading(true);
    setDownloadingAttachments((prev) => [...prev, attachmentId]);

    try {
      if (chatType === "liveChat") {
        Emitter.emit(API_DOWNLOAD_ATTACHMENT, {
          body: { contactId, attachmentId, attachmentName: name },
        });
      } else {
        await chatHistoryAttachment(contactId, attachmentId);
      }
    } catch (error) {
      console.error("Error downloading attachment:", error);
      showErrorToast("Error in downloading attachment", "attachmentError");
    } finally {
      setIsDownloading(false);
    }
  };

  const renderBody = (message: ChatMessage) => {
    const attachments = Array.isArray(message.text) ? message.text : [];
    const content = typeof message.text === "string" ? message.text : "";

    if (
      Array.isArray(attachments) &&
      attachments.length > 0 &&
      attachments[0].AttachmentId
    ) {
      return attachments.map((attachment: any, idx: number) => (
        <View key={idx}>
          {isDownloading ? (
            <Text>
              <Flex alignItems={"center"} gap="size-100">
                <strong>{attachment.AttachmentName}</strong>
                <DownloadIcon
                  color={message.role === "CUSTOMER" ? "#0265dc" : "#FFF"}
                />
                <ProgressCircle
                  size="S"
                  aria-label="Loading…"
                  isIndeterminate
                  marginStart="size-100"
                />
              </Flex>
            </Text>
          ) : (
            <a
              href="#"
              className="chatAttachmentLink"
              data-testid="download-attachment"
              onClick={(e) =>
                downloadAttachment(
                  e,
                  attachment.AttachmentId,
                  chat?.id,
                  attachment.AttachmentName
                )
              }
            >
              <Flex alignItems={"center"} gap="size-100">
                <strong>{attachment.AttachmentName}</strong>
                <DownloadIcon
                  color={message.role === "CUSTOMER" ? "#0265dc" : "#FFF"}
                />
              </Flex>
            </a>
          )}
        </View>
      ));
    }

    const regex = /(https?:\/\/[^\s]+)/g;
    const parts = content.split(regex);
    const isLink = (text: string) => /^(https?:\/\/[^\s]+)/.test(text);

    return (
      <Text>
        {parts.map((part: string, i: number) =>
          isLink(part) ? (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="chatMessageLink"
            >
              {part + " "}
            </a>
          ) : (
            <React.Fragment key={i}>{part + " "}</React.Fragment>
          )
        )}
      </Text>
    );
  };

  const renderMessage = (message: any, index: React.Key | null | undefined) => {
    const isCustomer = message.role === CHAT_ROLES.CUSTOMER;
    const className = isCustomer
      ? "chat-transcript-white"
      : "chat-transcript-blue";
    const name = isCustomer
      ? chat?.customer?.name
      : `${agent.firstName} ${agent.lastName}`;

    return (
      <React.Fragment key={index}>
        <View width="100%">
          {isCustomer && (
            <Flex UNSAFE_className="chat-transcript-header" alignItems="center">
              <Flex
                UNSAFE_className="chat-agent-avatar"
                UNSAFE_style={{
                  backgroundColor,
                  borderRadius: "50%",
                  marginRight: "8px",
                }}
              >
                <Text UNSAFE_className="chat-agent-text">{initials}</Text>
              </Flex>
              <Text UNSAFE_className="chat-transcript-cname">{name}</Text>
            </Flex>
          )}
        </View>
        {message.text && (
          <>
            <View
              UNSAFE_className={className}
              alignSelf={isCustomer ? "flex-start" : "flex-end"}
              marginBottom={12}
            >
              <View UNSAFE_className="chat-transcript-chat-text">
                {renderBody(message)}
              </View>
            </View>
            <Text
              UNSAFE_className={
                isCustomer
                  ? "chat-timestamp-text-white"
                  : "chat-timestamp-text-blue"
              }
            >
              {formatTimeToMMSS(message.timestamp)}
            </Text>
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <View height="100%" UNSAFE_className="chat-transcript-section">
      {loadingTranscript &&
        chat?.self?.state === CHAT_STATES.ESTABLISHED &&
        common.tab !== "History" && (
          <Flex justifyContent="center">
            <ProgressCircle size="S" aria-label="Loading…" isIndeterminate />
          </Flex>
        )}
      {chat?.self?.state === CHAT_STATES.ESTABLISHED && (
        <Flex justifyContent={"end"}>
          <Text UNSAFE_className="agent-join">
            Agent joined the conversation
          </Text>
        </Flex>
      )}
      {transcript &&
        transcript.map((message: IndividualChat, index: number) =>
          renderMessage(message, index)
        )}
      {messages &&
        messages.map((message: any, index: React.Key | null | undefined) => {
          const isAlreadyInTranscript =
            transcript &&
            transcript.some((msg: any) => msg.messageId === message.id);
          return !isAlreadyInTranscript && renderMessage(message, index);
        })}
      {uplodingFiles &&
        Object.entries(uplodingFiles).map(([attachmentId, files]: any) =>
          files.map((file: any, fileIdx: number) => (
            <React.Fragment key={`upload-${attachmentId}-${fileIdx}`}>
              <View
                UNSAFE_className="chat-transcript-blue"
                alignSelf="flex-end"
                marginBottom={20}
              >
                <Text>
                  {file.attachmentName}{" "}
                  <ProgressCircle
                    size="S"
                    marginStart="size-100"
                    aria-label="Uploading…"
                    isIndeterminate
                  />
                </Text>
              </View>
              <Text UNSAFE_className="chat-timestamp-text-blue">
                Sending...
              </Text>
            </React.Fragment>
          ))
        )}
      {typingStatus && (
        <Text
          UNSAFE_style={{
            marginTop: "auto",
            paddingTop: "0.5rem",
          }}
        >
          {chat?.customer?.name} is typing...
        </Text>
      )}
      <div ref={chatEndRef} style={{ height: "1px" }} />
    </View>
  );
};

export default ChatTranscript;
